// BrandHeader — reusable logo + wordmark component for Citycare Property.
//
// Three visual variants:
//   topbar  — compact horizontal layout for app headers (default)
//   sidebar — slightly smaller, for nav sidebars
//   splash  — large centred layout for login / splash screens
//
// Design contract:
//   - SVG mark hardcoded at /citycare-logo.svg (apps/frontend/public/).
//     The previous VITE_LOGO_URL env-var indirection was removed
//     because the brand asset is bundled with the app, not
//     per-tenant white-label. Inline <img> so jsdom can render
//     without DOM tricks.
//   - Wordmark: "Citycare Property" (visible text, not aria-label)
//   - Optional subtitle for context disambiguation (e.g. "Admin Console")
//   - No opinionated background — inherits from parent
//   - Text colour: --color-ink (brand token from W1.2)
//   - Mark is aria-hidden="true"; wordmark is real visible text

export type BrandHeaderVariant = 'topbar' | 'sidebar' | 'splash'

// Hardcoded per product direction — the live Citycare brand mark lives
// at apps/frontend/public/citycare-logo.svg. Drop the official asset
// over that file (same path) to swap without a code change. The
// previous VITE_LOGO_URL env-var indirection was removed because the
// asset is bundled with the app, not per-tenant white-label.
const CITYCARE_LOGO_SRC = '/citycare-logo.svg'

export interface BrandHeaderProps {
  /** Visual size / layout variant. Defaults to `topbar`. */
  variant?: BrandHeaderVariant
  /** Whether to render the "Citycare Property" wordmark. Defaults to `true`. */
  showWordmark?: boolean
  /** Optional product context shown below the wordmark, e.g. "Admin Console". */
  subtitle?: string
  /** Escape hatch for layout-specific spacing. */
  className?: string
}

// Pixel dimensions for the SVG mark per variant
const MARK_SIZE: Record<BrandHeaderVariant, number> = {
  topbar: 28,
  sidebar: 24,
  splash: 64,
}

// Wordmark text-size Tailwind classes per variant. Citycare brand
// uses Tiempos Headline / Fraunces for the wordmark (skill: display
// serif on every brand surface). Splash keeps text-2xl for the
// brand-header behavioural contract; sm:text-3xl scales up on tablets
// and desktop to match the 32px+ display sizes called for in the
// brand type scale.
const WORDMARK_CLASS: Record<BrandHeaderVariant, string> = {
  topbar: 'font-display text-base font-semibold tracking-tight',
  sidebar: 'font-display text-sm font-semibold tracking-tight',
  splash: 'font-display text-2xl sm:text-3xl font-bold tracking-tight',
}

// Subtitle text-size per variant (slightly smaller than wordmark)
const SUBTITLE_CLASS: Record<BrandHeaderVariant, string> = {
  topbar: 'text-xs',
  sidebar: 'text-xs',
  splash: 'text-base',
}

// Gap between mark and wordmark block
const GAP_CLASS: Record<BrandHeaderVariant, string> = {
  topbar: 'gap-2',
  sidebar: 'gap-2',
  splash: 'gap-4',
}

export function BrandHeader({
  variant = 'topbar',
  showWordmark = true,
  subtitle,
  className,
}: BrandHeaderProps) {
  const isCenter = variant === 'splash'
  const markSize = MARK_SIZE[variant]

  const containerClasses = [
    'inline-flex items-center',
    isCenter ? 'flex-col text-center' : 'flex-row',
    GAP_CLASS[variant],
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={containerClasses}>
      <img
        src={CITYCARE_LOGO_SRC}
        alt=""
        aria-hidden="true"
        width={markSize}
        height={markSize}
        className="shrink-0"
      />

      {showWordmark && (
        <div className={isCenter ? 'flex flex-col items-center' : 'flex flex-col'}>
          <span
            className={[
              WORDMARK_CLASS[variant],
              'leading-tight text-current',
            ].join(' ')}
          >
            Citycare Property
          </span>

          {subtitle && (
            <span
              className={[
                SUBTITLE_CLASS[variant],
                // Citycare brand v1.0: muted ink (Forest @ 60%) reads
                // correctly on any of the brand's light surfaces and
                // keeps the subtitle subordinate to the wordmark.
                'text-ink/60 leading-tight',
              ].join(' ')}
            >
              {subtitle}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
